// GestionFormationAdmin.jsx - Gestion des formations avec pagination
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { APP_CONFIG } from '../../config/appConfig';
import ModuleManager from './ModuleManager';
import DemoViewer from '../../components/communs/DemoViewer';
import { Eye, Plus, Edit, Trash2, FolderOpen, BookOpen, ChevronDown, ChevronRight, Search } from 'lucide-react';

export default function GestionFormationAdmin() {
    const { token } = useAuth();
    const [formations, setFormations] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedClients, setExpandedClients] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [searchInput, setSearchInput] = useState('');

    // Pagination
    const [pagination, setPagination] = useState({
        page: 0,
        size: 10,
        totalElements: 0,
        totalPages: 0
    });

    // Modals
    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingFormation, setEditingFormation] = useState(null);
    const [showModulesModal, setShowModulesModal] = useState(false);
    const [selectedFormation, setSelectedFormation] = useState(null);

    // État pour la démo de formation
    const [showFormationDemo, setShowFormationDemo] = useState(false);
    const [demoFormationId, setDemoFormationId] = useState(null);

    // Formulaire
    const [formData, setFormData] = useState({
        titre: '',
        description: '',
        clientId: '',
        code: ''
    });
    const [formErrors, setFormErrors] = useState({ code: '', titre: '', clientId: '' });

    // Récupération des clients de l'admin (sans pagination, on prend tout)
    const fetchClients = useCallback(async () => {
        if (!token) return;
        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/clients?page=0&size=100`, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Accept": "application/json"
                }
            });
            if (response.ok) {
                const data = await response.json();
                setClients(data.data || data.content || data || []);
            }
        } catch (err) {
            console.error("Erreur récupération clients:", err);
        }
    }, [token]);

    // Récupération des formations paginée
    const fetchFormations = useCallback(async (page = 0) => {
        if (!token) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        try {
            let url = `${APP_CONFIG.BASE_URL}/api/v1/admin/formations?page=${page}&size=${pagination.size}`;
            if (searchTerm) {
                url += `&search=${encodeURIComponent(searchTerm)}`;
            }

            const response = await fetch(url, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Accept": "application/json"
                }
            });

            if (!response.ok) {
                if (response.status === 401) throw new Error('Session expirée');
                if (response.status === 403) throw new Error('Accès refusé');
                throw new Error(`Erreur HTTP ${response.status}`);
            }

            const data = await response.json();

            // Gestion des différents formats de réponse
            let formationsData = [];
            let pageInfo = {};

            if (data.content) {
                // Format Page Spring
                formationsData = data.content;
                pageInfo = {
                    page: data.number || 0,
                    size: data.size || pagination.size,
                    totalElements: data.totalElements || 0,
                    totalPages: data.totalPages || 0
                };
            } else if (Array.isArray(data)) {
                // Fallback : tableau brut (sans pagination) -> on simule une page unique
                formationsData = data;
                pageInfo = {
                    page: 0,
                    size: data.length,
                    totalElements: data.length,
                    totalPages: 1
                };
            } else if (data.status === 'SUCCESS' && data.data) {
                formationsData = data.data;
                pageInfo = {
                    page: data.page || 0,
                    size: data.size || pagination.size,
                    totalElements: data.totalElements || formationsData.length,
                    totalPages: data.totalPages || 1
                };
            } else {
                formationsData = [];
                pageInfo = { page: 0, size: pagination.size, totalElements: 0, totalPages: 0 };
            }

            setFormations(formationsData);
            setPagination(prev => ({
                ...prev,
                ...pageInfo,
                page: page // pour synchroniser
            }));
        } catch (err) {
            console.error("Erreur API FormationAdmin:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [token, pagination.size, searchTerm]);

    // Chargement initial et changement de page/recherche
    useEffect(() => {
        fetchClients();
        fetchFormations(0);
    }, []);

    // Recharger quand la recherche change (reset page à 0)
    useEffect(() => {
        fetchFormations(0);
    }, [searchTerm]);

    // Fonctions de navigation
    const goToPage = (newPage) => {
        if (newPage >= 0 && newPage < pagination.totalPages) {
            fetchFormations(newPage);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setSearchTerm(searchInput);
    };

    // Ouvrir la démo de la formation
    const openFormationDemo = (formation) => {
        setDemoFormationId(formation.id);
        setShowFormationDemo(true);
    };

    // Validation du formulaire
    const validateForm = () => {
        const errors = { code: '', titre: '', clientId: '' };
        let isValid = true;

        if (!formData.code || formData.code.trim() === '') {
            errors.code = 'Le code de la formation est obligatoire';
            isValid = false;
        } else if (formData.code.length < 3) {
            errors.code = 'Le code doit contenir au moins 3 caractères';
            isValid = false;
        }

        if (!formData.titre || formData.titre.trim() === '') {
            errors.titre = 'Le titre de la formation est obligatoire';
            isValid = false;
        }

        if (!formData.clientId || formData.clientId === '') {
            errors.clientId = 'Veuillez sélectionner un client';
            isValid = false;
        }

        setFormErrors(errors);
        return isValid;
    };

    // Vérifier l'unicité du code (parmi les formations chargées)
    const isCodeUnique = (code, excludeId = null) => {
        return !formations.some(f => 
            f.code && f.code.toLowerCase() === code.toLowerCase() && 
            (excludeId === null || f.id !== excludeId)
        );
    };

    // Création d'une formation
    const handleCreate = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;
        if (!isCodeUnique(formData.code)) {
            setFormErrors(prev => ({ ...prev, code: 'Ce code est déjà utilisé par une autre formation' }));
            return;
        }

        setLoading(true);
        setError(null);
		console.error("Donnes a presenter au Backend :", formData);
        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/formations`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    code: formData.code.trim(),
                    titre: formData.titre.trim(),
                    description: formData.description?.trim() || '',
                    clientId:  parseInt(formData.clientId) 
                })
            });
            
            const data = await response.json();
            if (response.ok) {
                setShowModal(false);
                resetForm();
                fetchFormations(pagination.page);
                alert("✅ Formation créée avec succès");
            } else {
                if (data.error && data.error.includes('code')) {
                    setFormErrors(prev => ({ ...prev, code: data.error }));
                } else {
                    setError(data.error || "Erreur lors de la création");
                    alert(`❌ ${data.error || "Erreur lors de la création"}`);
                }
            }
        } catch (err) {
            console.error("Erreur création:", err);
            setError("Erreur technique lors de la création");
            alert("❌ Erreur technique lors de la création");
        } finally {
            setLoading(false);
        }
    };

    // Mise à jour d'une formation
    const handleUpdate = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;
        if (!isCodeUnique(formData.code, editingFormation.id)) {
            setFormErrors(prev => ({ ...prev, code: 'Ce code est déjà utilisé par une autre formation' }));
            return;
        }

        setLoading(true);
        setError(null);
		//console.error("Donnes a presenter au Backend :", formData);
		//console.error("Donnes a presenter au Backend numeric  :", parseInt(formData.clientId) );
        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/formations/${editingFormation.id}`, {
                method: "PUT",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    code: formData.code.trim(),
                    titre: formData.titre.trim(),
                    description: formData.description?.trim() || '',
                    clientId: parseInt(formData.clientId) 
                })
            });

            const data = await response.json();

            if (response.ok) {
                setShowEditModal(false);
                setEditingFormation(null);
                resetForm();
                fetchFormations(pagination.page);
                alert("✅ Formation modifiée avec succès");
            } else {
                if (data.error && data.error.includes('code')) {
                    setFormErrors(prev => ({ ...prev, code: data.error }));
                } else {
                    setError(data.error || "Erreur lors de la modification");
                    alert(`❌ ${data.error || "Erreur lors de la modification"}`);
                }
            }
        } catch (err) {
            console.error("Erreur modification:", err);
            setError("Erreur technique lors de la modification");
            alert("❌ Erreur technique lors de la modification");
        } finally {
            setLoading(false);
        }
    };

    // Suppression
    const handleDelete = async (formation) => {
        if (!window.confirm(`⚠️ Êtes-vous sûr de vouloir supprimer la formation "${formation.titre}" ?\nTous les modules associés seront également supprimés.`)) return;

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/formations/${formation.id}`, {
                method: "DELETE",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Accept": "application/json"
                }
            });

            if (response.ok) {
                fetchFormations(pagination.page);
                alert("✅ Formation supprimée avec succès");
            } else {
                const data = await response.json();
                setError(data.error || "Erreur lors de la suppression");
                alert(`❌ ${data.error || "Erreur lors de la suppression"}`);
            }
        } catch (err) {
            console.error("Erreur suppression:", err);
            setError("Erreur technique lors de la suppression");
            alert("❌ Erreur technique lors de la suppression");
        } finally {
            setLoading(false);
        }
    };

    // Ouvrir les modals
    const openEditModal = (formation) => {
        setEditingFormation(formation);
        const clientId = getClientIdFromFormation(formation);
        setFormData({
            titre: formation.titre || '',
            description: formation.description || '',
            clientId: clientId ? clientId.toString() : '',
            code: formation.code || ''
        });
        setFormErrors({ code: '', titre: '', clientId: '' });
        setShowEditModal(true);
    };

    const openModulesModal = (formation) => {
        setSelectedFormation(formation);
        setShowModulesModal(true);
    };

    const resetForm = () => {
        setFormData({
            titre: '',
            description: '',
            clientId: '',
            code: ''
        });
        setFormErrors({ code: '', titre: '', clientId: '' });
        setError(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Fonctions utilitaires
    const getClientIdFromFormation = (formation) => {
        if (!formation) return null;
        if (formation.client && typeof formation.client === 'object' && formation.client.id) return formation.client.id;
        if (typeof formation.client === 'number') return formation.client;
        if (formation.clientId) return formation.clientId;
        return null;
    };

    const getClientName = (clientId) => {
        if (!clientId) return 'N/A';
        const client = clients.find(c => c.id === clientId);
        return client ? client.nomEntreprise : 'N/A';
    };

    const getClientCode = (clientId) => {
        if (!clientId) return '';
        const client = clients.find(c => c.id === clientId);
        return client ? client.code : '';
    };

    const getFormationsByClient = () => {
        const groups = {};
        formations.forEach(formation => {
            const clientId = getClientIdFromFormation(formation);
            if (!clientId) return;
            if (!groups[clientId]) {
                groups[clientId] = {
                    clientId,
                    clientName: getClientName(clientId),
                    clientCode: getClientCode(clientId),
                    formations: []
                };
            }
            groups[clientId].formations.push(formation);
        });
        return Object.values(groups).sort((a, b) => a.clientName.localeCompare(b.clientName));
    };

    const toggleClientExpand = (clientId) => {
        setExpandedClients(prev => ({
            ...prev,
            [clientId]: !prev[clientId]
        }));
    };

    const isClientExpanded = (clientId) => {
        return expandedClients[clientId] !== false;
    };

    if (!token) {
        return (
            <div className="p-6 text-center">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-md mx-auto">
                    <p className="text-amber-700">Authentification requise pour gérer les formations</p>
                </div>
            </div>
        );
    }

    const clientGroups = getFormationsByClient();

    return (
        <div className="space-y-4 p-4 w-[70%] max-w-full mx-auto">
            {/* En-tête */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-gray-200 pb-3">
                <div>
                    <h2 className="text-xl font-bold text-gray-900 tracking-tight">
                        Gestion des Formations
                    </h2>
                    <p className="text-xs text-gray-500">
                        Arborescence des formations par client
                    </p>
                </div>
                <div className="flex flex-col sm:flex-row items-center gap-2">
                    <form onSubmit={handleSearch} className="flex gap-2">
                        <input
                            type="text"
                            placeholder="Rechercher..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent w-40"
                        />
                        <button type="submit" className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-colors">
                            <Search size={16} />
                        </button>
                    </form>
                    <button
                        onClick={() => {
                            resetForm();
                            setShowModal(true);
                        }}
                        className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors flex items-center gap-2"
                    >
                        <Plus size={16} />
                        Nouvelle formation
                    </button>
                </div>
            </div>

            {/* Message d'erreur */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <strong className="text-red-800">Erreur :</strong> <span className="text-red-700">{error}</span>
                </div>
            )}

            {/* Arborescence des formations */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {loading && formations.length === 0 ? (
                    <div className="text-center py-8">
                        <div className="flex justify-center">
                            <svg className="animate-spin h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">Chargement...</p>
                    </div>
                ) : clientGroups.length === 0 ? (
                    <div className="text-center py-8">
                        <div className="text-4xl mb-3">📚</div>
                        <p className="text-sm text-gray-400">Aucune formation trouvée</p>
                        <p className="text-xs text-gray-400 mt-1">Cliquez sur "Nouvelle formation" pour commencer</p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {clientGroups.map(group => (
                            <div key={group.clientId} className="p-1.5">
                                {/* Nœud client */}
                                <div 
                                    className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                                    onClick={() => toggleClientExpand(group.clientId)}
                                >
                                    <span className="text-gray-500 text-sm">
                                        {isClientExpanded(group.clientId) ? 
                                            <ChevronDown size={14} /> : 
                                            <ChevronRight size={14} />
                                        }
                                    </span>
                                    <FolderOpen size={16} className="text-green-600 flex-shrink-0" />
                                    <span className="font-semibold text-gray-900 text-sm">
                                        {group.clientName}
                                    </span>
                                    {group.clientCode && (
                                        <span className="text-xs text-gray-500 font-mono">
                                            ({group.clientCode})
                                        </span>
                                    )}
                                    <span className="text-xs text-gray-400 ml-2">
                                        ({group.formations.length} formation{group.formations.length > 1 ? 's' : ''})
                                    </span>
                                </div>

                                {/* Liste des formations du client */}
                                {isClientExpanded(group.clientId) && (
                                    <div className="ml-8 pl-4 border-l-2 border-gray-200 space-y-0.5 mt-0.5">
                                        {group.formations.map(formation => {
                                            const moduleCount = formation.modules?.length || 0;
                                            
                                            return (
                                                <div 
                                                    key={formation.id} 
                                                    className="flex items-center justify-between p-1.5 rounded-lg hover:bg-gray-50 transition-colors group"
                                                >
                                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                                        <BookOpen size={14} className="text-emerald-600 flex-shrink-0" />
                                                        <span className="font-medium text-gray-800 text-sm truncate">
                                                            {formation.titre}
                                                        </span>
                                                        {formation.code && (
                                                            <span className="text-[10px] text-gray-400 font-mono flex-shrink-0">
                                                                [{formation.code}]
                                                            </span>
                                                        )}
                                                        <span className="text-[10px] text-gray-500 truncate hidden sm:block max-w-xs">
                                                            {formation.description || 'Aucune description'}
                                                        </span>
                                                        <span className="text-[10px] text-gray-400 ml-auto flex-shrink-0">
                                                            {moduleCount} module{moduleCount > 1 ? 's' : ''}
                                                        </span>
                                                    </div>

                                                    <div className="flex gap-0.5 ml-2 flex-shrink-0">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openFormationDemo(formation);
                                                            }}
                                                            className="p-1 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
                                                            title="Prévisualiser la formation"
                                                        >
                                                            <Eye size={14} />
                                                        </button>

                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openModulesModal(formation);
                                                            }}
                                                            className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors"
                                                            title="Gérer les modules"
                                                        >
                                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                                            </svg>
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openEditModal(formation);
                                                            }}
                                                            className="p-1 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                                            title="Modifier"
                                                        >
                                                            <Edit size={14} />
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDelete(formation);
                                                            }}
                                                            className="p-1 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                            title="Supprimer"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-3 px-4 py-3 bg-gray-50 border-t border-gray-200">
                        <span className="text-xs text-gray-500">
                            {pagination.totalElements} formation(s) - Page {pagination.page + 1}/{pagination.totalPages}
                        </span>
                        <div className="flex gap-1.5">
                            <button
                                onClick={() => goToPage(pagination.page - 1)}
                                disabled={pagination.page === 0}
                                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                ← Précédent
                            </button>
                            <button
                                onClick={() => goToPage(pagination.page + 1)}
                                disabled={pagination.page + 1 >= pagination.totalPages}
                                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                Suivant →
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* ======================================== */}
            {/* MODAL : CRÉATION D'UNE FORMATION */}
            {/* ======================================== */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl z-10">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                <span className="text-green-600">✦</span>
                                Nouvelle formation
                            </h3>
                            <button 
                                onClick={() => { setShowModal(false); resetForm(); }} 
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                ×
                            </button>
                        </div>
                        <form onSubmit={handleCreate} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Code *
                                    <span className="text-red-500 ml-1">(obligatoire)</span>
                                </label>
                                <input
                                    type="text"
                                    name="code"
                                    placeholder="Code unique de la formation (ex: FORM-001)"
                                    required
                                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                                        formErrors.code ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    value={formData.code || ''}
                                    onChange={handleInputChange}
                                />
                                {formErrors.code && (
                                    <p className="text-red-500 text-xs mt-1">{formErrors.code}</p>
                                )}
                                <p className="text-xs text-gray-400 mt-1">
                                    Le code doit être unique et identifier la formation (minimum 3 caractères)
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Titre *
                                    <span className="text-red-500 ml-1">(obligatoire)</span>
                                </label>
                                <input
                                    type="text"
                                    name="titre"
                                    placeholder="Titre de la formation"
                                    required
                                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                                        formErrors.titre ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    value={formData.titre}
                                    onChange={handleInputChange}
                                />
                                {formErrors.titre && (
                                    <p className="text-red-500 text-xs mt-1">{formErrors.titre}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <textarea
                                    name="description"
                                    placeholder="Description de la formation (optionnelle)"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    rows="2"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Client *
                                    <span className="text-red-500 ml-1">(obligatoire)</span>
                                </label>
                                <select
                                    name="clientId"
                                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                                        formErrors.clientId ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    value={formData.clientId}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Sélectionner un client</option>
                                    {Array.isArray(clients) && clients.map(client => (
                                        <option key={client.id} value={client.id}>
                                            {client.nomEntreprise} {client.code ? `(${client.code})` : ''}
                                        </option>
                                    ))}
                                </select>
                                {formErrors.clientId && (
                                    <p className="text-red-500 text-xs mt-1">{formErrors.clientId}</p>
                                )}
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => { setShowModal(false); resetForm(); }}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {loading ? 'Création...' : 'Créer'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ======================================== */}
            {/* MODAL : MODIFICATION D'UNE FORMATION */}
            {/* ======================================== */}
            {showEditModal && editingFormation && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl z-10">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                <span className="text-green-600">✎</span>
                                Modifier la formation
                            </h3>
                            <button 
                                onClick={() => { setShowEditModal(false); setEditingFormation(null); resetForm(); }} 
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                ×
                            </button>
                        </div>
                        <form onSubmit={handleUpdate} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Code *
                                    <span className="text-red-500 ml-1">(obligatoire)</span>
                                </label>
                                <input
                                    type="text"
                                    name="code"
                                    placeholder="Code unique de la formation"
                                    required
                                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                                        formErrors.code ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    value={formData.code || ''}
                                    onChange={handleInputChange}
                                />
                                {formErrors.code && (
                                    <p className="text-red-500 text-xs mt-1">{formErrors.code}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Titre *
                                    <span className="text-red-500 ml-1">(obligatoire)</span>
                                </label>
                                <input
                                    type="text"
                                    name="titre"
                                    placeholder="Titre de la formation"
                                    required
                                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                                        formErrors.titre ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    value={formData.titre || ''}
                                    onChange={handleInputChange}
                                />
                                {formErrors.titre && (
                                    <p className="text-red-500 text-xs mt-1">{formErrors.titre}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <textarea
                                    name="description"
                                    placeholder="Description de la formation"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    rows="2"
                                    value={formData.description || ''}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Client *
                                    <span className="text-red-500 ml-1">(obligatoire)</span>
                                </label>
                                <select
                                    name="clientId"
                                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                                        formErrors.clientId ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    value={formData.clientId || ''}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Sélectionner un client</option>
                                    {Array.isArray(clients) && clients.map(client => (
                                        <option key={client.id} value={client.id}>
                                            {client.nomEntreprise} {client.code ? `(${client.code})` : ''}
                                        </option>
                                    ))}
                                </select>
                                {formErrors.clientId && (
                                    <p className="text-red-500 text-xs mt-1">{formErrors.clientId}</p>
                                )}
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => { setShowEditModal(false); setEditingFormation(null); resetForm(); }}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {loading ? 'Mise à jour...' : 'Mettre à jour'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ======================================== */}
            {/* MODAL : GESTION DES MODULES */}
            {/* ======================================== */}
            {showModulesModal && selectedFormation && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                <span className="text-green-600">📚</span>
                                Modules de : {selectedFormation.titre}
                            </h3>
                            <button 
                                onClick={() => { setShowModulesModal(false); setSelectedFormation(null); }} 
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                ×
                            </button>
                        </div>
                        <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
                            <ModuleManager 
                                formationId={selectedFormation.id}
                                clientId={selectedFormation?.client?.id || selectedFormation?.clientId}  
                                token={token}
                                onModuleChange={() => fetchFormations(pagination.page)}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* ======================================== */}
            {/* MODAL : DÉMONSTRATION DE LA FORMATION */}
            {/* ======================================== */}
            {showFormationDemo && demoFormationId && (
                <DemoViewer
                    formationId={demoFormationId}
                    token={token}
                    onClose={() => {
                        setShowFormationDemo(false);
                        setDemoFormationId(null);
                    }}
                    isOpen={showFormationDemo}
                    userRole="admin"
                />
            )}
        </div>
    );
}